<template>
  <div
    ref="switcherRef"
    class="relative"
    :class="props.variant === 'mobile' ? 'w-full' : 'block'"
  >
    <button
      type="button"
      class="flex items-center gap-2 border border-gray-200 rounded-full px-3 py-1 text-sm text-dark-teal-600 focus:outline-none focus:ring-2 focus:ring-dark-teal-600 transition-colors duration-200 hover:bg-light-pink-100 w-full"
      :aria-expanded="isOpen"
      :aria-controls="listboxId"
      :aria-label="currentAriaLabel"
      @click="toggleDropdown"
    >
      <FlagPicture :code="selectedCode" :label="currentLanguageLabel" />
      <span class="font-medium hidden md:block">{{ currentLanguageLabel }}</span>
      <svg
        class="h-4 w-4 transition-transform duration-200"
        :class="{ 'rotate-180': isOpen }"
        viewBox="0 0 12 12"
        fill="none"
        stroke="currentColor"
      >
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M3 4l3 4 3-4" />
      </svg>
    </button>

    <transition name="fade">
      <ul
        v-if="isOpen"
        :id="listboxId"
        role="listbox"
        class="absolute right-0 mt-2 w-12 md:w-48 bg-white border border-gray-200 rounded-lg shadow-lg z-50 py-1"
      >
        <li v-for="available in availableLocales" :key="available.code">
          <button
            type="button"
            role="option"
            class="flex items-center gap-3 w-full px-3 py-2 text-sm text-left hover:bg-light-pink-100 transition-colors duration-150"
            :class="{
              'bg-light-pink-100 text-dark-teal-600 font-semibold': available.code === selectedCode,
              'text-dark-teal-600': available.code !== selectedCode
            }"
            :aria-selected="available.code === selectedCode"
            @click="selectLocale(available.code)"
          >
            <FlagPicture :code="available.code" :label="getLanguageLabel(available.code)" />
            <span class="hidden md:block flex-1">{{ getLanguageLabel(available.code) }}</span>
            <svg
              v-if="available.code === selectedCode"
              class="h-4 w-4 text-dark-teal-600"
              viewBox="0 0 20 20"
              fill="none"
              stroke="currentColor"
            >
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 10l3 3 7-7" />
            </svg>
          </button>
        </li>
      </ul>
    </transition>
  </div>
</template>

<script setup lang="ts">
import { computed, defineComponent, h, onBeforeUnmount, onMounted, ref, watch, type ComputedRef } from 'vue'
import { useI18n, useSwitchLocalePath, useRouter, useId, navigateTo } from '#imports'
import type { LocaleObject } from '@nuxtjs/i18n'

const FLAG_COUNTRY_MAP: Record<string, string> = {
  en: 'gb',
  de: 'de',
  fr: 'fr',
  it: 'it',
  es: 'es',
  nl: 'nl',
  ja: 'jp',
  zh: 'cn',
  ko: 'kr',
  sv: 'se'
}

const getFlagCountryCode = (code: string) => (FLAG_COUNTRY_MAP[code] || code || '').toLowerCase()

const FlagPicture = defineComponent({
  name: 'FlagPicture',
  props: {
    code: {
      type: String,
      required: true
    },
    label: {
      type: String,
      required: true
    }
  },
  setup(props) {
    const size = 20
    const doubleSize = size * 2
    return () => {
      const country = getFlagCountryCode(props.code)
      const base = `https://flagcdn.com/w${size}/${country}`
      const double = `https://flagcdn.com/w${doubleSize}/${country}`
      return h(
        'picture',
        { class: 'inline-flex items-center justify-center w-5' },
        [
          h('source', {
            type: 'image/webp',
            srcset: `${base}.webp, ${double}.webp 2x`
          }),
          h('source', {
            type: 'image/png',
            srcset: `${base}.png, ${double}.png 2x`
          }),
          h('img', {
            src: `${base}.png`,
            width: size,
            height: Math.round(size * 0.75),
            alt: props.label,
            loading: 'lazy',
            decoding: 'async',
            class: 'rounded-sm shadow-sm'
          })
        ]
      )
    }
  }
})

const props = defineProps<{
  variant?: 'desktop' | 'mobile'
}>()

const emit = defineEmits<{
  (e: 'locale-selected', code: string): void
}>()

const { t, locale, locales, setLocale } = useI18n()
const router = useRouter()
const switchLocalePath = useSwitchLocalePath()
const idBase = useId()
const listboxId = `${idBase}-language-listbox`

const availableLocales: ComputedRef<LocaleObject[]> = computed(() => (locales.value as LocaleObject[]) || [])
const isOpen = ref(false)
const switcherRef = ref<HTMLElement | null>(null)
const selectedCode = ref<string>(locale.value)

watch(locale, (newCode) => {
  selectedCode.value = newCode
})

const getLanguageLabel = (code: string) => t(`common.languageNames.${code}`) || code.toUpperCase()

const currentLanguageLabel = computed(() => getLanguageLabel(selectedCode.value))
const currentAriaLabel = computed(() => t('common.localeSwitcher.current', { language: currentLanguageLabel.value }))

const toggleDropdown = () => {
  isOpen.value = !isOpen.value
}

const closeDropdown = () => {
  isOpen.value = false
}

const handleClickOutside = (event: MouseEvent) => {
  if (!isOpen.value || !switcherRef.value) {
    return
  }
  if (!switcherRef.value.contains(event.target as Node)) {
    closeDropdown()
  }
}

const selectLocale = async (code: string) => {
  if (!code || code === selectedCode.value) {
    closeDropdown()
    return
  }
  
  // Get the localized path first
  const localizedPath = switchLocalePath(code as LocaleObject['code'])
  
  if (localizedPath && localizedPath !== router.currentRoute.value.path) {
    // Use navigateTo instead of router.push to work better with i18n navigation guards
    // This prevents infinite redirect loops by letting Nuxt handle the navigation properly
    try {
      // Don't call setLocale separately - navigateTo with localizedPath will trigger i18n navigation guard
      // which will handle the locale change automatically
      await navigateTo(localizedPath)
      selectedCode.value = code
    } catch (error) {
      // If navigation fails or is aborted, fallback to setLocale only
      // This can happen if navigation is prevented or redirected
      if (error && typeof error === 'object' && 'message' in error && error.message !== 'Navigation aborted') {
        console.warn('Locale switch navigation failed, falling back to setLocale only:', error)
      }
      await setLocale(code)
      selectedCode.value = code
    }
  } else {
    // If path is the same or unavailable, just set locale
    await setLocale(code)
    selectedCode.value = code
  }
  
  emit('locale-selected', code)
  closeDropdown()
}

onMounted(() => {
  document.addEventListener('click', handleClickOutside)
})

onBeforeUnmount(() => {
  document.removeEventListener('click', handleClickOutside)
})
</script>

<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease, transform 0.2s ease;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
  transform: translateY(-4px);
}
</style>


